import { formatRelative } from '../lib/format.js';
import type { AnalysisDto } from '../types.js';

interface Props {
  analysis: AnalysisDto | null;
  /** Optional class to merge onto the wrapper. */
  className?: string;
}

/**
 * Compact AI provenance label (batch 18).
 *
 * Renders a tiny grey strip of the form:
 *
 *     Mock · 2h ago · confidence 0.72
 *
 * Used in the compact intelligence strip and in the right-rail AI
 * sidecar — anywhere the analysis is summarized rather than expanded.
 *
 * Rules:
 *   - Provider falls back to "Analysis" when no provider field is
 *     present (defensive against pre-batch-4 fixtures).
 *   - Confidence segment is omitted entirely when the structured
 *     output is null OR confidence is not a finite number.
 *   - Timestamp uses the same `formatRelative` helper as the rest of
 *     the dashboard for visual consistency.
 *   - Never surfaces prompt_messages or raw_response. The DTO already
 *     excludes them, but this component is also a barrier — if a
 *     future change adds those fields, no rendering site will leak
 *     them through here.
 */
export function AiProvenance({ analysis, className = '' }: Props) {
  if (analysis === null) return null;
  const provider = providerLabel(analysis);
  const when = formatRelative(analysis.created_at);
  const confidence = pickConfidence(analysis);
  return (
    <span
      data-testid="ai-provenance"
      className={`whitespace-nowrap text-[10px] text-zinc-500 ${className}`.trim()}
    >
      <span data-testid="ai-provenance-provider">{provider}</span>
      <span className="mx-1" aria-hidden="true">
        ·
      </span>
      <span data-testid="ai-provenance-when">{when}</span>
      {confidence !== null ? (
        <>
          <span className="mx-1" aria-hidden="true">
            ·
          </span>
          <span data-testid="ai-provenance-confidence">confidence {confidence.toFixed(2)}</span>
        </>
      ) : null}
    </span>
  );
}

function providerLabel(a: AnalysisDto): string {
  const p = (a.provider ?? '').trim();
  if (!p) return 'Analysis';
  // Title-case "mock" → "Mock"; leave other providers (anthropic,
  // openai, etc.) as the operator-recognizable identifier.
  return p.charAt(0).toUpperCase() + p.slice(1);
}

function pickConfidence(a: AnalysisDto): number | null {
  const c = a.structured_output?.confidence;
  if (typeof c !== 'number' || !Number.isFinite(c)) return null;
  return c;
}
